import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {NumberInput, Textarea, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {DateTimePicker} from '@mantine/dates';
import type {CourseClassroomRequest} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {useCreateCourseClassroomMutation} from 'src/redux/queries/courseClassroom';
import {useGetCourseDefinitionQuery} from 'src/redux/queries/courseDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {TrainingIcon} from 'src/app/training/coursePills';
import type {ISelectOption} from 'src/types/Form';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

/**
 * Class creation — schedule fields only. Roster is built afterwards in the
 * Class Workspace via the roster builder (add-by-assignment), never here.
 * Wave 4 (docs/plans/training-admin-overhaul.md).
 */
export const CreateCourseClassroomPage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {definitionId} = useParams();
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const {
    data: definition,
    isLoading: isDefinitionLoading,
    error: definitionError,
  } = useGetCourseDefinitionQuery(parseInt(definitionId!), {
    skip: !definitionId,
  });

  const [startsAt, setStartsAt] = useState<Date | null>(null);
  const [endsAt, setEndsAt] = useState<Date | null>(null);
  const [location, setLocation] = useState('');
  const [instructorId, setInstructorId] = useState<string | undefined>(
    undefined
  );
  const [capacity, setCapacity] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [isSavePending, setIsSavePending] = useState(false);

  const [createCourseClassroom] = useCreateCourseClassroomMutation();

  useEffect(() => {
    if (definitionError) {
      setAlert(parseErrorMessage(definitionError), 'error');
    }
  }, [definitionError]);

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'create-classroom',
    definitionId,
    courseName: definition?.name,
  });

  const handleSave = async () => {
    if (definitionId === undefined) {
      return;
    }

    try {
      setIsSavePending(true);

      const model: CourseClassroomRequest = {
        courseDefinitionId: parseInt(definitionId),
        assignmentIds: [],
        startsAt: startsAt ?? undefined,
        endsAt: endsAt ?? undefined,
        location: location.trim() || undefined,
        instructorId: instructorId ? Number(instructorId) : undefined,
        capacity: capacity !== '' ? capacity : undefined,
        notes: notes.trim() || undefined,
        // Legacy scheduling field still read by CourseCompletionCalculator -
        // keep it aligned with the real startsAt column.
        classroomAt: startsAt ?? new Date(),
      };

      const response = await createCourseClassroom(model).unwrap();

      setAlert('Classroom was successfully created.', 'success');
      navigate(
        PathUtility.TrainingCourseClassroomView(
          String(definitionId),
          String(response.id),
          'roster'
        ),
        {replace: true}
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box>
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
        />
        {definitionError ? (
          <Alert color="red" title="Error">
            Failed to load course. Please try again later.
          </Alert>
        ) : isDefinitionLoading ? (
          <Spinner loadingText="Loading...." />
        ) : (
          <>
            <PageHeader>
              <PageHeader.Title icon={TrainingIcon}>
                New {definition?.name} Class
              </PageHeader.Title>
              <PageHeader.Actions>
                <PageHeader.Action
                  variant="save"
                  onClick={handleSave}
                  loading={isSavePending}>
                  Save
                </PageHeader.Action>
              </PageHeader.Actions>
            </PageHeader>

            <SectionCard title="Schedule">
              <Box p="md">
                <Stack gap="md">
                  <SimpleGrid cols={{base: 1, sm: 2}} spacing="md">
                    <DateTimePicker
                      label="Starts"
                      placeholder="Select start date/time"
                      value={startsAt}
                      onChange={setStartsAt}
                      clearable
                      valueFormat="MM/DD/YYYY hh:mm A"
                    />
                    <DateTimePicker
                      label="Ends"
                      placeholder="Select end date/time"
                      value={endsAt}
                      onChange={setEndsAt}
                      clearable
                      minDate={startsAt ?? undefined}
                      valueFormat="MM/DD/YYYY hh:mm A"
                    />
                  </SimpleGrid>
                  <TextInput
                    label="Location"
                    placeholder="e.g. Building 4 Training Room"
                    value={location}
                    onChange={event => setLocation(event.currentTarget.value)}
                  />
                  <SimpleGrid cols={{base: 1, sm: 2}} spacing="md">
                    <PersonSelectBox
                      label="Instructor"
                      placeholder="Select instructor"
                      value={instructorId}
                      onSelectChangeCallback={(value: ISelectOption | null) =>
                        setInstructorId(
                          value?.id ? String(value.id) : undefined
                        )
                      }
                    />
                    <NumberInput
                      label="Capacity"
                      placeholder="e.g. 20"
                      min={1}
                      value={capacity}
                      onChange={value =>
                        setCapacity(value === '' ? '' : Number(value))
                      }
                    />
                  </SimpleGrid>
                  <Textarea
                    label="Notes"
                    placeholder="Anything the trainer or roster builder should know"
                    minRows={3}
                    value={notes}
                    onChange={event => setNotes(event.currentTarget.value)}
                  />
                </Stack>
              </Box>
            </SectionCard>

            <Group justify="flex-end" mt="md">
              <SaveButton loading={isSavePending} onClick={handleSave} />
            </Group>
          </>
        )}
      </Box>
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.TrainingCourseClassroomCreate(':definitionId'),
  element: <CreateCourseClassroomPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.Create,
  isPrivate: true,
});
